import type {
  ClientLfaConversion,
  ClientLfaConversionData,
} from '~/components/page/LFA/types';
import {
  type Date__Input,
  type Lfa_Service_Feedback,
  type Lfa_Conversion_Data_Rejection_Criteria,
  type Lfa_Service_Rejection_Criteria,
  Lfa_Service_Acceptance_Status,
  Lfa_Service_Acceptance_Type,
} from '~/graphql/types';

export type FeedbackType = {
  serviceVersion: number;
  type: Lfa_Service_Acceptance_Type;
  answer: Lfa_Service_Acceptance_Status;
  rejectionCriteria: Array<Lfa_Conversion_Data_Rejection_Criteria>;
};

export type UpdateFeedbackArgs = {
  serviceId: string;
  feedback: Partial<FeedbackType>;
};

export const createInitialState = (
  selectedServices: ClientLfaConversion['selectedServices'],
): State => ({
  serviceFeedback: new Map(),
  selectedServices,
  showAdditionalServices: false,
  snoozeUntil: null,
  snoozeNote: '',
  additionalComments: '',
  initialized: false,
});

export type State = {
  serviceFeedback: Map<string, FeedbackType>;
  selectedServices: ClientLfaConversion['selectedServices'];
  showAdditionalServices: boolean;
  snoozeUntil: Date__Input | null;
  snoozeNote: string;
  additionalComments: string;
  initialized: boolean;
};

export type ReducerAction =
  | {
      type: 'INITIALIZE_FEEDBACK';
      payload: {
        services: ClientLfaConversion['selectedServices'];
        lastFeedback?: Array<Lfa_Service_Feedback> | null;
      };
    }
  | { type: 'UPDATE_SERVICE_FEEDBACK'; payload: UpdateFeedbackArgs }
  | {
      type: 'ADD_SERVICE';
      payload: {
        service: ClientLfaConversionData['enabledServices'][number];
        lastFeedback?: Array<Lfa_Service_Feedback> | null;
      };
    }
  | {
      type: 'REMOVE_SERVICE';
      payload: {
        service: ClientLfaConversion['selectedServices'][number];
      };
    }
  | { type: 'TOGGLE_ADDITIONAL_SERVICES' }
  | { type: 'SET_SNOOZE_UNTIL'; payload: { date: Date__Input | null } }
  | { type: 'SET_SNOOZE_NOTE'; payload: { note: string } }
  | { type: 'SET_ADDITIONAL_COMMENTS'; payload: { comments: string } }
  | { type: 'RESET_STATE' };

export const INITIAL_FEEDBACK: FeedbackType = {
  answer: Lfa_Service_Acceptance_Status.Accepted,
  rejectionCriteria: [],
  serviceVersion: 1,
  type: Lfa_Service_Acceptance_Type.Appointment,
};

const mapRejectionCriteria = ({
  prevCriteria,
  currentCriteria,
}: {
  prevCriteria: Array<Lfa_Service_Rejection_Criteria>;
  currentCriteria: Array<Lfa_Conversion_Data_Rejection_Criteria>;
}): Array<Lfa_Conversion_Data_Rejection_Criteria> =>
  prevCriteria.map(c => {
    const found = currentCriteria.find(option => option.id === c.id);
    return {
      __typename: 'LFA_Conversion_Data_Rejection_Criteria',
      id: c.id,
      label: found?.label ?? c.reason ?? '',
      reason: c.reason,
    };
  });

const reducer = (state: State, action: ReducerAction): State => {
  switch (action.type) {
    case 'INITIALIZE_FEEDBACK': {
      if (state.initialized) {
        return state;
      }
      const feedbackMap = new Map<string, FeedbackType>();
      const { services, lastFeedback } = action.payload;

      services.forEach(service => {
        if (service.acceptance?.[0]) {
          const previousFeedback = lastFeedback?.find(
            fb => fb.id === service.id,
          );
          const initialAnswer =
            previousFeedback?.acceptance[0].status ?? INITIAL_FEEDBACK.answer;
          const initialRejectionCriteria = mapRejectionCriteria({
            prevCriteria:
              previousFeedback?.acceptance[0].rejectionCriteria ?? [],
            currentCriteria: service.acceptance[0].reason ?? [],
          });
          feedbackMap.set(service.id, {
            serviceVersion: service.version,
            type: service.acceptance[0].type,
            answer: initialAnswer,
            rejectionCriteria: initialRejectionCriteria,
          });
        }
      });
      return {
        ...state,
        serviceFeedback: feedbackMap,
        selectedServices: services,
        initialized: true,
      };
    }
    case 'UPDATE_SERVICE_FEEDBACK': {
      const { serviceId, feedback } = action.payload;
      const existingFeedback = state.serviceFeedback.get(serviceId);
      if (!existingFeedback) {
        return state;
      }
      const updatedFeedback = {
        ...existingFeedback,
        ...feedback,
      };
      const newFeedbackMap = new Map(state.serviceFeedback);
      newFeedbackMap.set(serviceId, updatedFeedback);

      return {
        ...state,
        serviceFeedback: newFeedbackMap,
      };
    }
    case 'ADD_SERVICE': {
      const { service, lastFeedback } = action.payload;
      if (state.selectedServices.some(s => s.id === service.id)) {
        return state;
      }
      const newFeedbackMap = new Map(state.serviceFeedback);
      if (service.acceptance?.[0]) {
        const previousFeedback = lastFeedback?.find(fb => fb.id === service.id);
        const initialAnswer =
          previousFeedback?.acceptance[0].status ?? INITIAL_FEEDBACK.answer;
        const initialRejectionCriteria = mapRejectionCriteria({
          prevCriteria: previousFeedback?.acceptance[0].rejectionCriteria ?? [],
          currentCriteria: service.acceptance[0].reason ?? [],
        });
        newFeedbackMap.set(service.id, {
          serviceVersion: service.version,
          type: service.acceptance[0].type,
          answer: initialAnswer,
          rejectionCriteria: initialRejectionCriteria,
        });
      }
      return {
        ...state,
        selectedServices: [
          // TODO: Remove typename overwrite once the backend is updated
          ...state.selectedServices,
          { ...service, __typename: 'LFA_Service_Details' },
        ],
        serviceFeedback: newFeedbackMap,
      };
    }
    case 'REMOVE_SERVICE': {
      const { service } = action.payload;
      // Check if service exists in selectedServices
      if (!state.selectedServices.some(s => s.id === service.id)) {
        return state;
      }

      // Remove service from selectedServices
      const updatedSelectedServices = state.selectedServices.filter(
        s => s.id !== service.id,
      );

      // Remove feedback for this service
      const newFeedbackMap = new Map(state.serviceFeedback);
      newFeedbackMap.delete(service.id);

      return {
        ...state,
        selectedServices: updatedSelectedServices,
        serviceFeedback: newFeedbackMap,
      };
    }
    case 'TOGGLE_ADDITIONAL_SERVICES':
      return {
        ...state,
        showAdditionalServices: !state.showAdditionalServices,
      };
    case 'SET_SNOOZE_UNTIL': {
      return {
        ...state,
        snoozeUntil: action.payload.date,
      };
    }
    case 'SET_SNOOZE_NOTE': {
      return {
        ...state,
        snoozeNote: action.payload.note,
      };
    }
    case 'SET_ADDITIONAL_COMMENTS':
      return {
        ...state,
        additionalComments: action.payload.comments,
      };
    case 'RESET_STATE':
      return {
        serviceFeedback: new Map(),
        selectedServices: [],
        showAdditionalServices: false,
        snoozeUntil: null,
        snoozeNote: '',
        additionalComments: '',
        initialized: false,
      };
    default:
      return state;
  }
};

export default reducer;
